import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, MapPin, Clock, RefreshCw, Loader2 } from "lucide-react";
import { fetchNWSActive, eventColor } from "../utils/nws";
import { Button } from "../components/ui/button";

const timeShort = (iso) => {
  if (!iso) return "\u2014";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) + " · " + d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch (_) { return "\u2014"; }
};

const Alerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [visible, setVisible] = useState(50);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = async () => {
    setLoading(true);
    const data = await fetchNWSActive();
    setAlerts(data);
    setLastUpdated(new Date());
    setLoading(false);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const counts = useMemo(() => {
    const c = { tor: 0, svr: 0, ff: 0, watch: 0, other: 0 };
    alerts.forEach((a) => {
      const e = (a.event || "").toLowerCase();
      if (e.includes("tornado warning")) c.tor++;
      else if (e.includes("severe thunderstorm warning")) c.svr++;
      else if (e.includes("flash flood") || e.includes("flood warning")) c.ff++;
      else if (e.includes("watch")) c.watch++;
      else c.other++;
    });
    return c;
  }, [alerts]);

  const filtered = useMemo(() => {
    if (filter === "All") return alerts;
    return alerts.filter((a) => (a.event || "").toLowerCase().includes(filter.toLowerCase()));
  }, [alerts, filter]);

  const filters = ["All", "Tornado Warning", "Severe Thunderstorm Warning", "Flash Flood", "Watch"];

  return (
    <div className="max-w-6xl mx-auto px-6 pt-10">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] mono uppercase tracking-widest text-red-400 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> Live NWS Alerts
          </div>
          <h1 className="display-italic text-4xl sm:text-5xl mt-1 uppercase sd-title-gradient">Active watches and warnings</h1>
          <p className="text-slate-400 text-sm mt-2">
            Auto-refreshed every 60 seconds from the National Weather Service.{" "}
            {lastUpdated && <span className="mono text-[11px] text-slate-500">Updated {lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>}
          </p>
        </div>
        <Button onClick={load} data-testid="alerts-refresh-button" variant="outline" className="border-white/15 bg-white/[0.03] hover:bg-white/[0.08] text-slate-100 gap-2 h-9">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
        </Button>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-6">
        {[
          { label: "Tornado", value: counts.tor, color: "bg-red-600" },
          { label: "Severe T-Storm", value: counts.svr, color: "bg-amber-500" },
          { label: "Flood", value: counts.ff, color: "bg-emerald-500" },
          { label: "Watches", value: counts.watch, color: "bg-cyan-500" },
          { label: "Other", value: counts.other, color: "bg-slate-500" },
        ].map((c) => (
          <div key={c.label} className="sd-panel rounded-lg px-3 py-2.5">
            <div className="flex items-center gap-2">
              <span className={`inline-block w-2.5 h-2.5 rounded-full ${c.color}`} />
              <div className="text-[10px] mono uppercase tracking-widest text-slate-400">{c.label}</div>
            </div>
            <div className="text-xl font-bold mono text-slate-100 mt-0.5">{c.value}</div>
          </div>
        ))}
      </div>

      {/* Filter chips */}
      <div className="mt-6 flex items-center gap-2 flex-wrap">
        {filters.map((f) => (
          <button
            key={f}
            data-testid={`alerts-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => { setFilter(f); setVisible(50); }}
            className={`px-3 py-1.5 rounded-full text-[12px] mono border ${
              filter === f
                ? "bg-red-600 text-white border-red-500"
                : "bg-white/[0.03] text-slate-300 border-white/10 hover:bg-white/[0.08]"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Alerts */}
      <div className="mt-6 space-y-3">
        {loading && filtered.length === 0 && (
          <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading alerts…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="sd-panel rounded-xl p-10 text-center">
            <div className="text-emerald-300 font-bold display-italic text-xl uppercase">ALL CLEAR</div>
            <div className="text-slate-400 text-sm mt-1">No active {filter !== "All" ? filter.toLowerCase() : ""} alerts across the country.</div>
          </div>
        )}
        {filtered.slice(0, visible).map((a, i) => {
          const c = eventColor(a.event);
          return (
            <div key={a.id} data-testid={`alert-card-${i}`} className="sd-panel rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-start gap-4 border-l-4" style={{ borderLeftColor: c.fill }}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md font-bold text-[11px] mono uppercase tracking-widest w-fit text-white" style={{ backgroundColor: c.fill }}>
                <AlertTriangle className="w-3.5 h-3.5" /> {a.event}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-100 break-words">{a.areaDesc || "—"}</div>
                {a.headline && (
                  <div className="text-sm text-slate-300 mt-1 line-clamp-3">{a.headline}</div>
                )}
                <div className="mt-2 flex items-center gap-3 flex-wrap text-[11px] mono text-slate-500">
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Issued {timeShort(a.sent)}</span>
                  <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> Expires {timeShort(a.expires)}</span>
                  {a.severity && <span>Severity: <span className="text-slate-300">{a.severity}</span></span>}
                  {a.certainty && <span>Certainty: <span className="text-slate-300">{a.certainty}</span></span>}
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length > visible && (
          <div className="pt-2 text-center">
            <Button
              data-testid="alerts-load-more-button"
              onClick={() => setVisible((count) => count + 50)}
              variant="outline"
              className="h-10 border-white/15 bg-white/[0.03] px-6 text-slate-100 hover:bg-white/[0.08]"
            >
              Show 50 more · {filtered.length - visible} remaining
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Alerts;
